import type { Hook } from "phoenix_live_view";

interface SimpleFormSubmitHook extends Hook {
  submitListener?: (event: SubmitEvent) => void;
}

/**
 * Parses FormData with bracket notation into nested objects.
 * Handles:
 * - "name" → { name: "value" }
 * - "parent[child]" → { parent: { child: "value" } }
 * - "ids[]" → { ids: ["v1", "v2"] }
 */
function serializeFormData(
  form: HTMLFormElement,
  submitter?: HTMLElement | null,
): Record<string, unknown> {
  const formData = new FormData(form);

  // Include the submit button's name/value if present
  if (submitter && submitter instanceof HTMLButtonElement && submitter.name) {
    formData.append(submitter.name, submitter.value || "");
  }

  const result: Record<string, unknown> = {};

  for (const [rawKey, value] of formData.entries()) {
    if (value instanceof File) continue;

    const isArray = rawKey.endsWith("[]");
    const cleanKey = isArray ? rawKey.slice(0, -2) : rawKey;
    const keys = cleanKey.replace(/\]/g, "").split("[");

    let current: any = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key];
    }

    const lastKey = keys[keys.length - 1];

    if (isArray) {
      if (!(lastKey in current) || !Array.isArray(current[lastKey])) {
        current[lastKey] = [];
      }
      (current[lastKey] as unknown[]).push(value);
    } else {
      current[lastKey] = value;
    }
  }

  return result;
}

/**
 * SimpleFormSubmit — A LiveView hook that handles form submissions
 * without LiveView's default phx-change behaviour.
 *
 * What it does:
 * - Stops the browser's default submit
 * - Converts FormData to a nested object (supporting bracket notation)
 * - Sends it once via pushEvent/pushEventTo on submit only
 *
 * Why:
 * phx-change sends events on every input update, which is expensive for
 * large forms. This hook avoids that by sending data only when submitted.
 *
 * Usage:
 * Add `phx-hook="SimpleFormSubmit"` to the form along with `phx-submit`.
 * Don't use `phx-change` with this hook.
 */

const SimpleFormSubmit: Partial<SimpleFormSubmitHook> = {
  mounted(this: SimpleFormSubmitHook) {
    const form = this.el as HTMLFormElement;

    this.submitListener = (event: SubmitEvent) => {
      event.preventDefault();
      const phxTarget = form.getAttribute("phx-target");
      const phxEvent = form.getAttribute("phx-submit");

      if (!phxEvent) {
        console.error(
          'SimpleFormSubmit hook requires a "phx-submit" attribute on the form element.',
          form,
        );
        return;
      }

      const payload = serializeFormData(form, event.submitter);

      if (phxTarget) {
        this.pushEventTo(phxTarget, phxEvent, payload);
      } else {
        this.pushEvent(phxEvent, payload);
      }
    };

    form.addEventListener("submit", this.submitListener);
  },

  destroyed(this: SimpleFormSubmitHook) {
    if (this.submitListener) {
      this.el.removeEventListener("submit", this.submitListener);
    }
  },
};

export default SimpleFormSubmit;
